import React, { useState } from 'react';

import { Action } from './types';
import { NextBestAction } from './types';
import type { ActionClick } from './types';
import { NextBestActions } from './NextBestActions';
import { getActionMapping } from './helpers/nextBestActions';

// AnswerActions component to handle different actions sources
interface AnswerActionsProps {
  actions?: Action[];
  nextBestActions?: NextBestAction[];
  isLastAnswer?: boolean;
  handleActionClick?: (action: ActionClick) => void;
  searchQueryId: string;
  searchIntentId?: string;
  question: string;
  answer: string;
}

const AnswerActions: React.FC<AnswerActionsProps> = ({
  actions,
  nextBestActions,
  isLastAnswer,
  handleActionClick,
  searchQueryId,
  searchIntentId,
  question,
  answer,
}) => {
  const hasActions = actions && actions.length > 0;
  const hasNextBestActions = nextBestActions && isLastAnswer && nextBestActions.length > 0;

  const [showAnswerActions] = useState(hasActions || hasNextBestActions);

  if (!showAnswerActions) {
    return null;
  }

  // Actions from the actions prop coming from intent match
  let mappedActions: NextBestAction[] = [];

  if (hasActions) {
    mappedActions = actions.map((action) => {
      // Attach searchIntentId to the action before mapping
      const actionWithIntent = {
        ...action,
        searchIntentId,
      };

      const mapping = getActionMapping(actionWithIntent);

      return {
        id: action.id,
        label: mapping.getLabel(actionWithIntent),
        type: mapping.type,
        icon: mapping.icon,
        value: mapping.getValue(actionWithIntent),
      };
    });
  }

  // Actions from the nextBestActions prop for default actions
  // only display actions if there are actions and it's the last answer
  if (hasNextBestActions) {
    // filter out nextBestActions that have the same type as the already mapped actions.
    // this is to avoid duplicate action types. eg. both the action and the next best action are "open_url"
    const filteredNextBestActions = nextBestActions.filter(
      (action) => !mappedActions.some((mappedAction) => mappedAction.type === action.type),
    );
    mappedActions = mappedActions.concat(filteredNextBestActions);
  }

  return (
    <div className="p-4">
      <NextBestActions
        nextBestActions={mappedActions}
        onActionClick={(action: NextBestAction, success?: boolean) => {
          if (action) {
            handleActionClick?.({
              action,
              searchQueryId: searchQueryId || '',
              query: question,
              answer,
              success,
            });
          }
        }}
      />
    </div>
  );
};

export default AnswerActions;
